/**
 * Print-Only Goal Setting Page for PDF Generation
 * URL: /print/goal-setting/:businessId
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../../print.css';

// ─────────────────────────────────────────────
//  Static data (mirrors GoalSettingModal.jsx)
// ─────────────────────────────────────────────
const CATEGORIES = [
  { id: 1,  roman: 'I',    label: 'Wealth Preservation & Growth' },
  { id: 2,  roman: 'II',   label: 'Estate & Legacy Planning' },
  { id: 3,  roman: 'III',  label: 'Family Governance & Unity' },
  { id: 4,  roman: 'IV',   label: 'Next Generation Development' },
  { id: 5,  roman: 'V',    label: 'Philanthropy & Social Impact' },
  { id: 6,  roman: 'VI',   label: 'Family Office Operations' },
  { id: 7,  roman: 'VII',  label: 'Business Ownership & Entrepreneurship' },
  { id: 8,  roman: 'VIII', label: 'Health, Wellness & Lifestyle' },
  { id: 9,  roman: 'IX',   label: 'Values, Faith & Purpose' },
  { id: 10, roman: 'X',    label: 'Legacy Protection & Continuity' },
];

const GOALS = [
  // I
  { id: 1,  category: 1,  tag: 'Investment',     title: 'Preserve Purchasing Power',                  objective: 'Protect and grow the real value of family assets, accounting for the effects of inflation over time.' },
  { id: 2,  category: 1,  tag: 'Investment',     title: 'Diversify Asset Allocation',                 objective: 'Spread investments across multiple asset classes to reduce risk and improve long-term stability.' },
  { id: 3,  category: 1,  tag: 'Tax Planning',   title: 'Optimize Tax Efficiency',                    objective: 'Use strategic planning tools to minimize overall tax burden through legal and efficient structures.' },
  { id: 4,  category: 1,  tag: 'Cash Mgmt',      title: 'Establish Liquidity Reserves',               objective: 'Keep sufficient liquid assets readily accessible to meet short- and medium-term financial needs.' },
  { id: 5,  category: 1,  tag: 'Risk Mgmt',      title: 'Implement Risk Management',                  objective: 'Put appropriate insurance and protection strategies in place to guard against significant losses.' },
  { id: 6,  category: 1,  tag: 'Governance',     title: 'Create a Family Investment Policy',          objective: 'Establish written guidelines for how family wealth is invested and managed over time.' },
  { id: 7,  category: 1,  tag: 'Investment',     title: 'Build Private Equity Exposure',              objective: 'Allocate a portion of investments to private equity or direct investments for enhanced returns.' },
  { id: 8,  category: 1,  tag: 'Real Estate',    title: 'Develop Real Estate Portfolio',              objective: 'Build a portfolio of income-producing real estate holdings diversified by type and location.' },
  { id: 9,  category: 1,  tag: 'Risk Mgmt',      title: 'Hedge Concentrated Positions',               objective: 'Reduce overexposure to any single investment, asset, or concentration of wealth.' },
  { id: 10, category: 1,  tag: 'Capital Access', title: 'Establish Family Bank',                      objective: 'Create a structured mechanism for family members to access capital for approved purposes.' },
  // II
  { id: 11, category: 2,  tag: 'Estate Tax',      title: 'Minimize Estate Tax Exposure',              objective: 'Use available legal structures to reduce the portion of wealth lost to estate taxes upon transfer.' },
  { id: 12, category: 2,  tag: 'Trust Planning',  title: 'Establish Dynasty Trust',                   objective: 'Create a long-term trust structure designed to hold and protect wealth across multiple generations.' },
  { id: 13, category: 2,  tag: 'Wealth Transfer', title: 'Implement Gifting Strategy',                objective: 'Develop a systematic strategy for transferring wealth to the next generation efficiently and tax-effectively.' },
  { id: 14, category: 2,  tag: 'Legacy',          title: 'Create Family Legacy Plan',                 objective: "Define the family's long-term legacy, including values, vision, and what future generations should stand for." },
  { id: 15, category: 2,  tag: 'Succession',      title: 'Plan Business Succession',                  objective: 'Create a clear plan for how leadership and ownership of the business will transition over time.' },
  { id: 16, category: 2,  tag: 'Philanthropy',    title: 'Establish Charitable Foundation',           objective: "Establish a formal vehicle for charitable giving aligned with the family's values and mission." },
  { id: 17, category: 2,  tag: 'Legacy',          title: 'Prepare Ethical Wills',                     objective: 'Document personal values, life lessons, and hopes to pass on to future family members.' },
  { id: 18, category: 2,  tag: 'Insurance',       title: 'Structure Life Insurance',                  objective: 'Use life insurance strategically to provide liquidity and ensure wealth transfers as intended.' },
  { id: 19, category: 2,  tag: 'Digital Estate',  title: 'Establish Digital Asset Plan',              objective: 'Identify and plan for all digital assets, accounts, and online presence as part of the estate plan.' },
  { id: 20, category: 2,  tag: 'Tax Planning',    title: 'Design Generation-Skipping Transfers',      objective: 'Structure transfers to benefit grandchildren and future generations in a tax-efficient manner.' },
  // III
  { id: 21, category: 3,  tag: 'Governance',    title: 'Establish Family Council',                    objective: 'Create a formal group of family representatives to guide collective decisions and family matters.' },
  { id: 22, category: 3,  tag: 'Governance',    title: 'Create Family Constitution',                  objective: 'Establish a written framework that defines how the family makes decisions and resolves differences.' },
  { id: 23, category: 3,  tag: 'Governance',    title: 'Implement Conflict Resolution',               objective: 'Put a structured process in place to address and resolve conflicts within the family constructively.' },
  { id: 24, category: 3,  tag: 'Unity',         title: 'Hold Annual Family Assembly',                 objective: 'Organize regular gatherings that bring the family together to communicate, align, and plan.' },
  { id: 25, category: 3,  tag: 'Communication', title: 'Create Family Communication Plan',            objective: 'Establish regular, consistent communication practices to keep all family members informed and connected.' },
  { id: 26, category: 3,  tag: 'Identity',      title: 'Develop Family Brand Identity',               objective: "Define a shared identity for the family, including values, symbols, and what the family stands for." },
  { id: 27, category: 3,  tag: 'Governance',    title: 'Establish Entry & Exit Policies',             objective: 'Set clear guidelines for how family members join or leave governance roles and structures.' },
  { id: 28, category: 3,  tag: 'Governance',    title: 'Create Advisory Board',                       objective: 'Bring in trusted outside perspectives through an independent advisory panel or board.' },
  { id: 29, category: 3,  tag: 'Heritage',      title: 'Document Family History',                     objective: "Preserve the family's story and history in a documented form to pass to future generations." },
  { id: 30, category: 3,  tag: 'Governance',    title: 'Formalize Decision-Making Framework',         objective: 'Establish a clear process for how decisions are made, including who has authority over what.' },
  // IV
  { id: 31, category: 4,  tag: 'Education',        title: 'Design Financial Education Curriculum',    objective: 'Build age-appropriate financial education into the upbringing of all family members.' },
  { id: 32, category: 4,  tag: 'Development',      title: 'Establish Mentorship Program',             objective: 'Connect younger family members with experienced guides to accelerate their personal and professional development.' },
  { id: 33, category: 4,  tag: 'Career Dev',        title: 'Create Internship & Apprenticeship Pipeline', objective: 'Create structured work and learning opportunities for younger family members in family enterprises.' },
  { id: 34, category: 4,  tag: 'Education',        title: 'Fund Educational Excellence',              objective: 'Ensure financial resources are in place to support education and professional development at all levels.' },
  { id: 35, category: 4,  tag: 'Leadership',       title: 'Develop Leadership Competencies',          objective: 'Develop leadership skills in family members through structured programs, coaching, and experience.' },
  { id: 36, category: 4,  tag: 'Entrepreneurship', title: 'Encourage Entrepreneurship',               objective: 'Support and fund entrepreneurial ambitions of the next generation with appropriate capital and guidance.' },
  { id: 37, category: 4,  tag: 'Development',      title: 'Establish Coming-of-Age Milestones',       objective: "Define meaningful transition points that mark a family member's growing financial responsibility and independence." },
  { id: 38, category: 4,  tag: 'Character',        title: 'Create Gap Year & Service Program',        objective: 'Provide opportunities for meaningful service, exploration, and character development outside the business.' },
  { id: 39, category: 4,  tag: 'Assessment',       title: 'Assess Rising Generation Readiness',       objective: 'Regularly evaluate the readiness and capabilities of the next generation for future leadership roles.' },
  { id: 40, category: 4,  tag: 'Networking',       title: 'Build Professional Networks',              objective: 'Help family members build connections and networks relevant to their interests and future roles.' },
  // V
  { id: 41, category: 5,  tag: 'Mission',      title: 'Define Philanthropic Mission',                 objective: 'Define the areas and causes where the family wants to make the greatest charitable impact.' },
  { id: 42, category: 5,  tag: 'Process',      title: 'Establish Grant-Making Process',               objective: 'Create a structured approach to evaluating and selecting recipients of charitable contributions.' },
  { id: 43, category: 5,  tag: 'Engagement',   title: 'Engage Next-Gen in Giving',                    objective: 'Actively involve younger family members in giving decisions to build values and engagement.' },
  { id: 44, category: 5,  tag: 'Investment',   title: 'Pursue Impact Investing',                      objective: 'Allocate a portion of the portfolio to investments that generate both financial and social returns.' },
  { id: 45, category: 5,  tag: 'Partnerships', title: 'Build Strategic Partnerships',                 objective: 'Develop ongoing relationships with select charitable organizations to maximize impact over time.' },
  { id: 46, category: 5,  tag: 'Measurement',  title: 'Measure Philanthropic Impact',                 objective: 'Track and evaluate the outcomes of charitable activities to improve effectiveness over time.' },
  { id: 47, category: 5,  tag: 'Education',    title: 'Create Scholarship Program',                   objective: "Create scholarship opportunities that reflect the family's values and support deserving individuals." },
  { id: 48, category: 5,  tag: 'Tax Strategy', title: 'Leverage Non-Cash Assets',                     objective: 'Use non-cash assets strategically to maximize both charitable impact and tax benefits.' },
  { id: 49, category: 5,  tag: 'Innovation',   title: 'Explore Program-Related Investments',          objective: 'Explore innovative ways to deploy capital in support of mission-aligned organizations.' },
  { id: 50, category: 5,  tag: 'Legacy',       title: 'Document Philanthropic Legacy',                objective: "Regularly document and communicate the family's charitable legacy and impact." },
  // VI
  { id: 51, category: 6,  tag: 'Structure',     title: 'Establish Single Family Office',              objective: 'Establish a professional structure to centrally manage family wealth, assets, and operations.' },
  { id: 52, category: 6,  tag: 'Talent',        title: 'Recruit Key Personnel',                       objective: 'Hire qualified professionals to manage the financial, investment, and operational needs of the family.' },
  { id: 53, category: 6,  tag: 'Technology',    title: 'Implement Technology Platform',               objective: 'Deploy appropriate technology to manage family finances, investments, reporting, and records efficiently.' },
  { id: 54, category: 6,  tag: 'Reporting',     title: 'Create Reporting Framework',                  objective: 'Create consistent financial reporting and communication practices for family stakeholders.' },
  { id: 55, category: 6,  tag: 'Operations',    title: 'Establish Vendor Management',                 objective: 'Develop a systematic approach to selecting, evaluating, and managing outside service providers.' },
  { id: 56, category: 6,  tag: 'Compliance',    title: 'Develop Compliance Program',                  objective: 'Implement policies and procedures to ensure legal, regulatory, and data security compliance.' },
  { id: 57, category: 6,  tag: 'Property Mgmt', title: 'Manage Family Real Property',                 objective: 'Professionally manage and maintain all family-owned real properties and assets.' },
  { id: 58, category: 6,  tag: 'Lifestyle',     title: 'Coordinate Concierge Services',               objective: 'Provide family members with coordinated support for personal logistics, travel, and lifestyle needs.' },
  { id: 59, category: 6,  tag: 'Security',      title: 'Implement Cybersecurity Protocol',            objective: 'Protect family data, communications, and assets from cyber threats through appropriate measures.' },
  { id: 60, category: 6,  tag: 'Efficiency',    title: 'Optimize Family Office Cost',                 objective: 'Regularly evaluate and improve the efficiency and cost-effectiveness of family operations.' },
  // VII
  { id: 61, category: 7,  tag: 'Value Creation',  title: 'Maximize Operating Business Value',         objective: 'Identify and pursue opportunities to grow the value of the business on an ongoing basis.' },
  { id: 62, category: 7,  tag: 'Exit Planning',   title: 'Prepare for Business Exit',                 objective: 'Develop a clear plan for how and when the business will eventually be transitioned or sold.' },
  { id: 63, category: 7,  tag: 'Diversification', title: 'Diversify Business Holdings',               objective: "Reduce dependence on any single business by diversifying the family's commercial interests." },
  { id: 64, category: 7,  tag: 'Governance',      title: 'Establish Board Governance',                objective: 'Install appropriate oversight structures to govern the business independently and professionally.' },
  { id: 65, category: 7,  tag: 'IP Protection',   title: 'Protect Intellectual Property',             objective: "Identify, protect, and maximize the value of the business's intellectual property and competitive advantages." },
  { id: 66, category: 7,  tag: 'Growth',          title: 'Build Strategic Alliances',                 objective: "Pursue partnerships and alliances that expand the business's market presence and capability." },
  { id: 67, category: 7,  tag: 'Talent',          title: 'Develop Key Employee Retention',            objective: 'Design programs to retain and motivate key employees who are critical to business success.' },
  { id: 68, category: 7,  tag: 'Policy',          title: 'Create Family Employment Policy',           objective: 'Establish clear, fair policies for the employment and compensation of family members in the business.' },
  { id: 69, category: 7,  tag: 'ESG',             title: 'Implement ESG Standards',                   objective: 'Adopt environmental, social, and governance standards that align with family values and stakeholder expectations.' },
  { id: 70, category: 7,  tag: 'M&A',             title: 'Build Acquisition Pipeline',                objective: 'Proactively identify and evaluate potential acquisition opportunities to grow through strategic transactions.' },
  // VIII
  { id: 71, category: 8,  tag: 'Health',       title: 'Establish Executive Health Programs',          objective: 'Ensure family members have access to high-quality preventive and ongoing health care.' },
  { id: 72, category: 8,  tag: 'Health Mgmt',  title: 'Coordinate Family Health Records',            objective: 'Create a centralized system for managing and accessing family medical information and records.' },
  { id: 73, category: 8,  tag: 'Wellness',      title: 'Promote Mental Health & Wellbeing',          objective: 'Support the emotional and psychological wellbeing of family members at all life stages.' },
  { id: 74, category: 8,  tag: 'Lifestyle',     title: 'Design Lifestyle Management',                 objective: 'Develop personalized plans for physical health, nutrition, and overall lifestyle balance for family members.' },
  { id: 75, category: 8,  tag: 'Experiences',   title: 'Plan Multi-Generational Travel',             objective: 'Create meaningful shared experiences that strengthen family bonds and create lasting memories.' },
  { id: 76, category: 8,  tag: 'Security',      title: 'Manage Family Security',                      objective: 'Assess and address personal and family security risks with appropriate protective measures.' },
  { id: 77, category: 8,  tag: 'Property',      title: 'Create Family Retreat Spaces',               objective: 'Create or maintain dedicated spaces where the family can gather, connect, and build traditions.' },
  { id: 78, category: 8,  tag: 'Care Planning', title: 'Support Special Needs Planning',             objective: 'Plan for the long-term care and support of any family members with special health or life needs.' },
  { id: 79, category: 8,  tag: 'Culture',       title: 'Promote Work-Life Integration',               objective: 'Cultivate a family culture that values personal wellbeing, balance, and time together.' },
  { id: 80, category: 8,  tag: 'Development',   title: 'Build Resilience & Adaptability',            objective: 'Build the capacity of family members to handle setbacks, change, and adversity with resilience.' },
  // IX
  { id: 81, category: 9,  tag: 'Values',       title: 'Articulate Core Family Values',               objective: "Identify and articulate the core values that guide the family's decisions and way of life." },
  { id: 82, category: 9,  tag: 'Faith',        title: 'Integrate Faith & Stewardship',               objective: "Connect the management of family wealth to the family's spiritual beliefs and sense of stewardship." },
  { id: 83, category: 9,  tag: 'Purpose',      title: 'Define Individual Purpose',                   objective: 'Support each family member in finding and living out a clear sense of personal purpose and calling.' },
  { id: 84, category: 9,  tag: 'Tradition',    title: 'Create Shared Family Rituals',                objective: 'Establish meaningful traditions and rituals that reinforce family identity and bonds over generations.' },
  { id: 85, category: 9,  tag: 'Heritage',     title: 'Mentor Through Storytelling',                 objective: 'Use stories and shared experiences to transmit values, wisdom, and family identity to future generations.' },
  { id: 86, category: 9,  tag: 'Civic',        title: 'Engage in Community Leadership',              objective: 'Encourage family members to contribute to their communities through leadership and service.' },
  { id: 87, category: 9,  tag: 'Transparency', title: 'Practice Transparent Stewardship',            objective: "Foster a culture of transparency around the family's financial position and stewardship decisions." },
  { id: 88, category: 9,  tag: 'Character',    title: 'Develop Moral Courage',                       objective: 'Create opportunities for family members to practice and strengthen ethical reasoning and decision-making.' },
  { id: 89, category: 9,  tag: 'Dialogue',     title: 'Foster Intergenerational Dialogue',           objective: 'Create regular opportunities for honest, meaningful conversation across generations within the family.' },
  { id: 90, category: 9,  tag: 'Alignment',    title: 'Align Wealth with Meaning',                   objective: "Regularly review how family resources and actions align with the stated values and purpose of the family." },
  // X
  { id: 91,  category: 10, tag: 'Strategy',       title: 'Develop 100-Year Plan',                    objective: 'Develop a long-range strategic vision for what the family wants to become and build over generations.' },
  { id: 92,  category: 10, tag: 'Heritage',        title: 'Establish Family Archives',               objective: 'Preserve important family documents, history, and artifacts for the benefit of future generations.' },
  { id: 93,  category: 10, tag: 'Reputation',      title: 'Build External Reputation',               objective: 'Cultivate a positive, well-regarded family reputation through thoughtful actions and community engagement.' },
  { id: 94,  category: 10, tag: 'Risk Mgmt',       title: 'Prepare for Black Swan Events',           objective: 'Develop plans for how the family will respond to major unexpected events or disruptions.' },
  { id: 95,  category: 10, tag: 'Protection',      title: 'Create Prenuptial Frameworks',            objective: 'Establish clear expectations and frameworks to protect family assets in the event of marriage transitions.' },
  { id: 96,  category: 10, tag: 'Diversification', title: 'Secure Jurisdictional Diversification',   objective: 'Consider building relationships and holdings in multiple jurisdictions to provide flexibility and protection.' },
  { id: 97,  category: 10, tag: 'Continuity',      title: 'Implement Knowledge Transfer',            objective: 'Ensure that critical knowledge, relationships, and institutional wisdom are captured and passed on.' },
  { id: 98,  category: 10, tag: 'Networking',      title: 'Build Peer Family Networks',              objective: 'Connect with other families of similar situations to share insights, experiences, and best practices.' },
  { id: 99,  category: 10, tag: 'Measurement',     title: 'Measure Family Wealth Beyond Dollars',    objective: "Define what family wealth truly means — beyond financial assets — and measure progress against it." },
  { id: 100, category: 10, tag: 'Renewal',         title: 'Commit to Perpetual Renewal',             objective: "Commit to regularly revisiting and renewing the family's strategies, structures, and priorities over time." },
];

// ─────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────
const STATUS_LABELS = {
  na:          'N/A',
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed:   'Completed',
};

const STATUS_STYLES = {
  na:          { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' },
  not_started: { background: '#dbeafe', color: '#1e40af', border: '1px solid #bfdbfe' },
  in_progress: { background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' },
  completed:   { background: '#d1fae5', color: '#065f46', border: '1px solid #a7f3d0' },
};

function getCategoryStats(ratingsMap, categoryId) {
  const goals = GOALS.filter(g => g.category === categoryId);
  const stats = { na: 0, not_started: 0, in_progress: 0, completed: 0 };
  goals.forEach(g => {
    const status = ratingsMap[g.id]?.current_status || 'not_started';
    stats[status] = (stats[status] || 0) + 1;
  });
  const score = goals.reduce((sum, g) => sum + (ratingsMap[g.id]?.priority_level || 0), 0);
  return { ...stats, score };
}

// ─────────────────────────────────────────────
//  Component
// ─────────────────────────────────────────────
const PrintGoalSetting = () => {
  const { businessId } = useParams();
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState(null);
  const [ratingsMap, setRatingsMap] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('token');
        const token = urlToken || localStorage.getItem('token') || sessionStorage.getItem('token');

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Business-Id': businessId,
        };

        const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';

        const [profileRes, goalsRes] = await Promise.all([
          fetch(`${base}/business/profile`, { headers }),
          fetch(`${base}/goal-settings/`, { headers }),
        ]);

        if (!profileRes.ok) throw new Error('Failed to fetch business profile');
        if (!goalsRes.ok)   throw new Error('Failed to fetch goal settings');

        const profileData = await profileRes.json();
        const goalsData   = await goalsRes.json();

        setBusiness(profileData.data?.profile || {});

        const map = {};
        (goalsData.data || []).forEach(s => {
          map[s.goal_id] = {
            current_status: s.current_status,
            priority_level: s.priority_level,
            notes: s.notes || '',
          };
        });
        setRatingsMap(map);
        setLoading(false);
      } catch (err) {
        console.error('[PrintGoalSetting] Error:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [businessId]);

  useEffect(() => {
    if (!loading && (business || error)) {
      document.body.setAttribute('data-pdf-ready', 'true');
      document.title = `${business?.business_name || 'Business'} — Goal Setting Report`;
    }
  }, [loading, business, error]);

  if (loading) {
    return (
      <div className="print-container" data-pdf-loading="true">
        <div className="loading">Loading goal setting data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="print-container" data-pdf-error="true">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  // Summary stats
  const totalCompleted  = Object.values(ratingsMap).filter(r => r.current_status === 'completed').length;
  const totalInProgress = Object.values(ratingsMap).filter(r => r.current_status === 'in_progress').length;
  const totalPriority   = Object.values(ratingsMap).reduce((s, r) => s + (r.priority_level || 0), 0);

  const categoryStats = CATEGORIES.map(cat => ({ ...cat, ...getCategoryStats(ratingsMap, cat.id) }));
  const topCat = categoryStats.reduce((best, c) => c.score > best.score ? c : best, categoryStats[0]);

  return (
    <div className="print-container" data-pdf-ready="true">

      {/* ── COVER PAGE ── */}
      <div className="print-page cover-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <div className="cover-header">
          <h1>Goal Setting Report</h1>
          <div className="report-type">100 Wealth &amp; Family Objectives</div>
        </div>
        <div className="cover-business-info">
          <h2>{business?.business_name || 'Business Name'}</h2>
          {business?.industry && <p className="industry">{business.industry}</p>}
        </div>
        <div className="cover-footer">
          <p>Generated {today}</p>
          <p className="confidential">Confidential Goal Setting Report</p>
        </div>
      </div>

      {/* ── INTRODUCTION PAGE ── */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Turning Vision into Action Through Purposeful Facilitation</h1>

        <div style={{ marginTop: '0.25in' }}>
          <h2 style={{ fontSize: '11pt', fontWeight: 700, color: '#1e3a8a', marginBottom: '0.15in' }}>Introduction</h2>

          <p style={{ fontSize: '9pt', lineHeight: 1.7, color: '#374151', marginBottom: '0.18in' }}>
            Families of significant wealth face a unique constellation of challenges that extend far beyond investment management. The preservation and purposeful deployment of wealth across multiple generations requires intentional planning across ten interconnected domains: wealth preservation, estate planning, family governance, next-generation development, philanthropy, family office operations, business ownership, health and lifestyle, values and purpose, and long-term legacy protection.
          </p>

          <p style={{ fontSize: '9pt', lineHeight: 1.7, color: '#374151', marginBottom: '0.18in' }}>
            This report presents 100 actionable goals and objectives organized into these ten strategic categories. Each goal represents a discrete area of focus, while the accompanying objective provides a clear, adaptable target that a family can pursue over a defined time horizon. Together, they form a comprehensive roadmap that can be customized to any family's unique circumstances, values, and aspirations.
          </p>

          <p style={{ fontSize: '9pt', lineHeight: 1.7, color: '#374151', marginBottom: '0.3in' }}>
            This framework is designed to serve as both a planning tool and an accountability mechanism. Families and their advisors can use it to identify gaps in current planning, prioritize initiatives, assign ownership, and track progress over time. Not every goal will be relevant to every family — the power lies in the deliberate process of evaluation, selection, and commitment.
          </p>

          <div style={{ borderTop: '2px solid #3b82f6', paddingTop: '0.2in' }}>
            <h3 style={{ fontSize: '10pt', fontWeight: 700, color: '#1e3a8a', marginBottom: '0.12in' }}>The Ten Strategic Categories</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.08in 0.3in' }}>
              {[
                { roman: 'I',    label: 'Wealth Preservation & Growth' },
                { roman: 'II',   label: 'Estate & Legacy Planning' },
                { roman: 'III',  label: 'Family Governance & Unity' },
                { roman: 'IV',   label: 'Next Generation Development' },
                { roman: 'V',    label: 'Philanthropy & Social Impact' },
                { roman: 'VI',   label: 'Family Office Operations' },
                { roman: 'VII',  label: 'Business Ownership & Entrepreneurship' },
                { roman: 'VIII', label: 'Health, Wellness & Lifestyle' },
                { roman: 'IX',   label: 'Values, Faith & Purpose' },
                { roman: 'X',    label: 'Legacy Protection & Continuity' },
              ].map(c => (
                <div key={c.roman} style={{ display: 'flex', alignItems: 'center', gap: '0.1in', padding: '0.06in 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ minWidth: '0.35in', fontWeight: 700, color: '#1e3a8a', fontSize: '8.5pt' }}>{c.roman}.</span>
                  <span style={{ fontSize: '8.5pt', color: '#374151' }}>{c.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── SUMMARY PAGE ── */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Executive Summary</h1>

        {/* Stat boxes */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.2in', marginBottom: '0.2in' }}>
          {[
            { label: 'Goals Completed',    value: totalCompleted,  bg: '#d1fae5', border: '#6ee7b7', text: '#065f46' },
            { label: 'In Progress',        value: totalInProgress, bg: '#fef3c7', border: '#fde68a', text: '#92400e' },
            { label: 'Total Priority Score', value: totalPriority, bg: '#e0e7ff', border: '#c7d2fe', text: '#3730a3' },
          ].map(s => (
            <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: '6px', padding: '0.15in', textAlign: 'center' }}>
              <div style={{ fontSize: '22pt', fontWeight: 700, color: s.text, lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: '7pt', color: s.text, marginTop: '4px', fontWeight: 600 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Top priority callout */}
        {topCat.score > 0 && (
          <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '6px', padding: '0.1in 0.15in', marginBottom: '0.2in', display: 'flex', alignItems: 'center', gap: '0.1in' }}>
            <span style={{ fontSize: '12pt', color: '#d97706' }}>★</span>
            <span style={{ fontSize: '8pt', color: '#92400e' }}>
              <strong>Highest Priority Area:</strong> Category {topCat.roman} — {topCat.label} ({topCat.score} pts)
            </span>
          </div>
        )}

        {/* Category summary table */}
        <table className="info-table" style={{ fontSize: '7.5pt' }}>
          <thead>
            <tr>
              <th style={{ width: '6%' }}>#</th>
              <th style={{ width: '36%' }}>Category</th>
              <th style={{ width: '10%', textAlign: 'center' }}>Priority Score</th>
              <th style={{ width: '12%', textAlign: 'center' }}>Completed</th>
              <th style={{ width: '12%', textAlign: 'center' }}>In Progress</th>
              <th style={{ width: '12%', textAlign: 'center' }}>Not Started</th>
              <th style={{ width: '12%', textAlign: 'center' }}>N/A</th>
            </tr>
          </thead>
          <tbody>
            {categoryStats.map(cat => (
              <tr key={cat.id}>
                <td style={{ fontWeight: 700, color: '#1e3a8a' }}>{cat.roman}</td>
                <td style={{ fontWeight: 600 }}>{cat.label}</td>
                <td style={{ textAlign: 'center', fontWeight: 700, color: '#4338ca' }}>{cat.score}</td>
                <td style={{ textAlign: 'center', color: '#065f46' }}>{cat.completed}</td>
                <td style={{ textAlign: 'center', color: '#92400e' }}>{cat.in_progress}</td>
                <td style={{ textAlign: 'center', color: '#374151' }}>{cat.not_started}</td>
                <td style={{ textAlign: 'center', color: '#9ca3af' }}>{cat.na}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── CATEGORY DETAIL PAGES (1 per category) ── */}
      <style>{`
        .goal-detail-table {
          width: 100%;
          border-collapse: collapse;
          margin: 2px 0;
          font-size: 6.5pt;
          background: transparent;
          table-layout: fixed;
        }
        .goal-detail-table th {
          background: #f9fafb;
          font-weight: 600;
          text-align: left;
          padding: 3px 5px;
          border: 1px solid #d1d5db;
          color: #374151;
          font-size: 6.5pt;
          line-height: 1.2;
        }
        .goal-detail-table td {
          padding: 3px 5px;
          border: 1px solid #e5e7eb;
          background: transparent;
          font-size: 6.5pt;
          line-height: 1.25;
          vertical-align: middle;
        }
        .goal-detail-table tr:nth-child(even) td {
          background: #fafafa;
        }
      `}</style>

      {CATEGORIES.map(cat => {
        const goals = GOALS.filter(g => g.category === cat.id);
        return (
          <div key={cat.id} className="print-page">
            <div className="pdf-header">
              <img src="/dominion-header.png" alt="Dominion Business Advisors" />
            </div>
            <h1 className="section-title" style={{ fontSize: '12pt', marginBottom: '0.05in' }}>
              Category {cat.roman}: {cat.label}
            </h1>

            <table className="goal-detail-table">
              <colgroup>
                <col style={{ width: '4%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '26%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '29%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Goal</th>
                  <th>Objective</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th style={{ textAlign: 'center' }}>Priority</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {goals.map(goal => {
                  const r = ratingsMap[goal.id] || {};
                  const status   = r.current_status || 'not_started';
                  const priority = r.priority_level  || null;
                  const notes    = r.notes            || '';
                  const statusStyle = STATUS_STYLES[status] || STATUS_STYLES.not_started;

                  return (
                    <tr key={goal.id}>
                      <td style={{ fontWeight: 600, color: '#6b7280', textAlign: 'center' }}>{goal.id}</td>
                      <td style={{ fontWeight: 600, color: '#111827' }}>{goal.title}</td>
                      <td style={{ color: '#4b5563' }}>{goal.objective}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ ...statusStyle, borderRadius: '8px', padding: '1px 5px', fontSize: '6pt', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {STATUS_LABELS[status]}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: priority ? '#4338ca' : '#d1d5db', fontSize: '7.5pt' }}>
                        {priority ? `${priority}/5` : '—'}
                      </td>
                      <td style={{ color: notes ? '#374151' : '#9ca3af', fontStyle: notes ? 'normal' : 'italic' }}>
                        {notes || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

export default PrintGoalSetting;
